import React, { useEffect, useRef, useCallback, memo, useState } from 'react';
import HintToast from './HintToast';

interface Block {
  element: HTMLInputElement;
  pos: { x: number; y: number };
  vel: { x: number; y: number };
}

const RadioBounce: React.FC = memo(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blocksRef = useRef<Block[]>([]);
  const animationRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const hasScrolledRef = useRef<boolean>(false);
  const [showHint, setShowHint] = useState(true);

  const createRadioButton = useCallback((x: number, y: number, velX: number, velY: number): Block => {
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.className = 'radio-button';
    
    // Use CSS transform for better performance
    radio.style.cssText = `
      position: absolute;
      width: 20px;
      height: 20px;
      left: 0;
      top: 0;
      transform: translate(${x}px, ${y}px);
      will-change: transform;
      pointer-events: none;
    `;
    
    containerRef.current?.appendChild(radio);
    
    return {
      element: radio,
      pos: { x, y },
      vel: { x: velX, y: velY }
    };
  }, []);

  const addBlocks = useCallback((count: number) => {
    const blocks = blocksRef.current;
    while (blocks.length < count) {
      const x = Math.random() * (window.innerWidth - 20);
      const y = Math.random() * (window.innerHeight - 20);
      const velX = (Math.random() - 0.5) * 4;
      const velY = (Math.random() - 0.5) * 4;
      blocks.push(createRadioButton(x, y, velX, velY));
    }
  }, [createRadioButton]);

  const removeBlocks = useCallback((count: number) => {
    const blocks = blocksRef.current;
    while (blocks.length > count) {
      const block = blocks.pop();
      if (block) {
        block.element.remove();
      }
    }
  }, []);

  // Throttle scroll events for better performance
  const handleScroll = useCallback(() => {
    const now = performance.now();
    if (now - lastTimeRef.current < 16) return; // ~60fps
    lastTimeRef.current = now;

    const scrollY = window.scrollY;
    const totalHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const maxScroll = totalHeight - windowHeight;

    const viewportArea = window.innerWidth * window.innerHeight;
    const maxBlocks = Math.floor((0.30 * viewportArea) / (20 * 20));
    const normalizedScroll = Math.min(1, Math.max(0, scrollY / maxScroll));
    const blockCount = Math.min(maxBlocks, Math.max(1, Math.floor(normalizedScroll * (maxBlocks - 1)) + 1));

    const blocks = blocksRef.current;
    if (blockCount > blocks.length) {
      addBlocks(blockCount);
    } else if (blockCount < blocks.length) {
      removeBlocks(blockCount);
    }
  }, []);

  const isColliding = useCallback((blockA: Block, blockB: Block): boolean => {
    const dx = blockA.pos.x - blockB.pos.x;
    const dy = blockA.pos.y - blockB.pos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < 20;
  }, []);

  const resolveCollision = useCallback((blockA: Block, blockB: Block) => {
    const dx = blockA.pos.x - blockB.pos.x;
    const dy = blockA.pos.y - blockB.pos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const overlap = (20 - distance) / 2;
    const normX = dx / distance;
    const normY = dy / distance;

    blockA.pos.x += normX * overlap;
    blockA.pos.y += normY * overlap;
    blockB.pos.x -= normX * overlap;
    blockB.pos.y -= normY * overlap;

    const tempVelX = blockA.vel.x;
    const tempVelY = blockA.vel.y;
    blockA.vel.x = blockB.vel.x;
    blockA.vel.y = blockB.vel.y;
    blockB.vel.x = tempVelX;
    blockB.vel.y = tempVelY;

    blockA.element.checked = true;
    blockB.element.checked = true;

    setTimeout(() => {
      blockA.element.checked = false;
      blockB.element.checked = false;
    }, 100);
  }, []);

  const animate = useCallback(() => {
    const blocks = blocksRef.current;
    const width = window.innerWidth - 20;
    const height = window.innerHeight - 20;
    
    blocks.forEach(block => {
      let hitBoundary = false;

      block.pos.x += block.vel.x;
      block.pos.y += block.vel.y;

      if (block.pos.x <= 0) {
        block.vel.x *= -1;
        block.pos.x = 0;
        hitBoundary = true;
      } else if (block.pos.x >= width) {
        block.vel.x *= -1;
        block.pos.x = width;
        hitBoundary = true;
      }
      if (block.pos.y <= 0) {
        block.vel.y *= -1;
        block.pos.y = 0;
        hitBoundary = true;
      } else if (block.pos.y >= height) {
        block.vel.y *= -1;
        block.pos.y = height;
        hitBoundary = true;
      }

      if (hitBoundary) {
        block.element.checked = true;
        setTimeout(() => {
          block.element.checked = false;
        }, 100);
      }

      // Use transform for better performance
      block.element.style.transform = `translate(${block.pos.x}px, ${block.pos.y}px)`;
    });

    // Check collisions every other frame for better performance
    for (let i = 0; i < blocks.length; i++) {
      for (let j = i + 1; j < blocks.length; j++) {
        if (isColliding(blocks[i], blocks[j])) {
          resolveCollision(blocks[i], blocks[j]);
        }
      }
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [isColliding, resolveCollision]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const container = containerRef.current;
    if (!container) return;

    // Reset state when component mounts
    setShowHint(true);
    hasScrolledRef.current = false;

    addBlocks(1);
    animate();
    
    // Use passive event listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Separate handler to hide hint on first scroll
    const hideHintOnScroll = () => {
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
        setShowHint(false);
      }
    };
    window.addEventListener('scroll', hideHintOnScroll, { passive: true });

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', hideHintOnScroll);
      
      blocksRef.current.forEach(block => {
        if (block.element.parentNode) {
          block.element.parentNode.removeChild(block.element);
        }
      });
    };
  }, [addBlocks, animate, handleScroll]);

  return (
    <>
      <div 
        ref={containerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 1000
        }}
      />
      
      {/* Scroll hint - bottom banner */}
      {showHint && <HintToast text="Scroll for more" />}
    </>
  );
});

RadioBounce.displayName = 'RadioBounce';

export default RadioBounce; 